/**
 * OrderDetailDialog - Dialog for viewing order details
 */
import React, { useEffect } from 'react';
import { useProjectStore } from '../../stores';
import type { Order } from '../../types';
import '../Dialogs/Dialog.css';

interface OrderDetailDialogProps {
  isOpen: boolean;
  order: Order | null;
  onClose: () => void;
}

export const OrderDetailDialog: React.FC<OrderDetailDialogProps> = ({
  isOpen,
  order,
  onClose
}) => {
  const { projects, fetchProjects } = useProjectStore();

  useEffect(() => {
    if (isOpen && projects.length === 0) {
      fetchProjects();
    }
  }, [isOpen, projects.length, fetchProjects]);

  if (!isOpen || !order) return null;

  const customerName = order.customer_type === 'individual'
    ? `${order.first_name} ${order.last_name}`
    : order.company_name;

  const getProjectName = (projectId?: number) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project?.name || `ID: ${projectId}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('sk-SK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      pending: { label: 'Čakajúca', color: '#ffc107' },
      approved: { label: 'Schválená', color: '#28a745' },
      rejected: { label: 'Zamietnutá', color: '#dc3545' },
      converted: { label: 'Konvertovaná', color: '#6c757d' }
    };
    const badge = badges[status] || { label: status, color: '#6c757d' };
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        backgroundColor: badge.color,
        color: 'white',
        fontSize: '0.85rem',
        fontWeight: 'bold'
      }}>
        {badge.label}
      </span>
    );
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      internet: 'Internet',
      internet_tv: 'Internet + TV',
      internet_tv_phone: 'Internet + TV + Telefón'
    };
    return labels[type] || type;
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="dialog-header">
          <h2>📋 Detail objednávky</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>

        <div className="dialog-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #eee'
          }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                {customerName}
              </h3>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                {order.order_number}
              </div>
            </div>
            <div>
              {getStatusBadge(order.status)}
            </div>
          </div>

          {/* Customer Information */}
          <section style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '1.1rem',
              color: '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '0.5rem'
            }}>
              👤 Informácie o zákazníkovi
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <strong>Typ:</strong> {order.customer_type === 'individual' ? 'Fyzická osoba' : 'Firma'}
              </div>
              {order.customer_type === 'individual' ? (
                <>
                  <div><strong>Meno:</strong> {order.first_name}</div>
                  <div><strong>Priezvisko:</strong> {order.last_name}</div>
                </>
              ) : (
                <>
                  <div><strong>Názov firmy:</strong> {order.company_name}</div>
                  {order.ico && <div><strong>IČO:</strong> {order.ico}</div>}
                  {order.dic && <div><strong>DIČ:</strong> {order.dic}</div>}
                  {order.dic_dph && <div><strong>IČ DPH:</strong> {order.dic_dph}</div>}
                </>
              )}
            </div>
          </section>

          {/* Address */}
          <section style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '1.1rem',
              color: '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '0.5rem'
            }}>
              📍 Adresa
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div><strong>Ulica:</strong> {order.address}</div>
              <div><strong>Mesto:</strong> {order.city}</div>
              <div><strong>PSČ:</strong> {order.zip_code}</div>
              {order.latitude && order.longitude && (
                <div>
                  <strong>GPS:</strong> {order.latitude.toFixed(6)}, {order.longitude.toFixed(6)}
                </div>
              )}
            </div>
          </section>

          {/* Contact */}
          <section style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '1.1rem',
              color: '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '0.5rem'
            }}>
              📞 Kontakt
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div><strong>Email:</strong> {order.email}</div>
              <div><strong>Telefón:</strong> {order.phone}</div>
            </div>
          </section>

          {/* Service */}
          <section style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '1.1rem',
              color: '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '0.5rem'
            }}>
              🌐 Služba
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div><strong>Typ služby:</strong> {getServiceTypeLabel(order.service_type)}</div>
              {order.requested_speed && (
                <div><strong>Požadovaná rýchlosť:</strong> {order.requested_speed}</div>
              )}
            </div>
          </section>

          {/* Notes */}
          {(order.customer_notes || order.internal_notes) && (
            <section style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ 
                margin: '0 0 0.75rem 0', 
                fontSize: '1.1rem',
                color: '#333',
                borderBottom: '1px solid #eee',
                paddingBottom: '0.5rem'
              }}>
                📝 Poznámky
              </h4>
              {order.customer_notes && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Poznámky zákazníka:</strong>
                  <div style={{ 
                    marginTop: '0.25rem',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontStyle: 'italic'
                  }}>
                    {order.customer_notes}
                  </div>
                </div>
              )}
              {order.internal_notes && (
                <div>
                  <strong>Interné poznámky:</strong>
                  <div style={{ 
                    marginTop: '0.25rem',
                    padding: '0.75rem',
                    backgroundColor: '#fff3cd',
                    borderRadius: '4px',
                    border: '1px solid #ffc107'
                  }}>
                    {order.internal_notes}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Dates */}
          <section style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '1.1rem',
              color: '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '0.5rem'
            }}>
              📅 Dátumy
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
              <div><strong>Vytvorené:</strong> {formatDate(order.order_date)}</div>
              {order.approved_date && (
                <div><strong>Schválené:</strong> {formatDate(order.approved_date)}</div>
              )}
            </div>
          </section>

          {/* Project Assignment */}
          {order.assigned_project_id && (
            <section style={{ marginBottom: '1.5rem' }}>
              <h4 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '1.1rem',
                color: '#333',
                borderBottom: '1px solid #eee',
                paddingBottom: '0.5rem'
              }}>
                🏘️ Priradený projekt
              </h4>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                fontWeight: 'bold',
                fontSize: '1.05rem'
              }}>
                {getProjectName(order.assigned_project_id)}
              </div>
            </section>
          )}
        </div>

        <div className="dialog-actions">
          <button onClick={onClose} className="btn-primary">
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

